import { useState, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Button,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  TypographyProps,
} from '@mui/material'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import SubmitLockButton from 'components/submit-lock-button/submit-lock-button.component'
import { skipToken } from '@reduxjs/toolkit/query'

import Edit from 'assets/icons/Edit.png'
import Delete from 'assets/icons/Delete.png'
import Message from 'assets/icons/Message.png'

import {
  InvoiceResponse,
  InvoiceType,
  isToBeInvoicedWorkspaceTab,
} from 'store/invoices/invoices.types'
import ConfirmationDialog from 'components/confirmation-dialog/confirmation-dialog.component'
import {
  useDeleteInvoiceByIdMutation,
  useListInvoicedocumentsQuery,
  useListPaymentDocumentsQuery,
  useUpdateInvoiceStatusMutation,
} from 'store/invoices/invoices.api'

import { StyledTableCell } from '../invoice-table-body'
import InvoiceTableEmailDialog from '../invoice-table-email-dialog/invoice-table-email-dialog'
import { useDispatch } from 'react-redux'
import {
  setSelectedInvoiceId,
  setSelectedOrderId,
} from 'store/invoices/invoices.slice'

type InvoiceTableCellProps = {
  disabled?: boolean
  invoice: InvoiceResponse
  handleViewInvoice: (tab?: number) => void
  invoiceType: InvoiceType
} & TypographyProps

const InvoiceTableActions = ({
  minWidth,
  align,
  disabled,
  invoice,
  handleViewInvoice,
  invoiceType,
}: InvoiceTableCellProps) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const listBasePath = pathname.startsWith('/factoring') ? '/factoring' : '/invoice'
  const [deleteInvoice] = useDeleteInvoiceByIdMutation()
  const [updateInvoiceStatus, { isLoading: isMarkPaidLoading }] =
    useUpdateInvoiceStatusMutation()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [moreMenuAnchor, setMoreMenuAnchor] = useState<null | HTMLElement>(null)
  const moreMenuOpen = Boolean(moreMenuAnchor)
  const { data: documents } = useListInvoicedocumentsQuery(
    invoice?.orderId ? { orderId: invoice.orderId } : skipToken
  )

  const { data: paymentDocuments } = useListPaymentDocumentsQuery(
    invoice?.orderId ? { orderId: invoice.orderId } : skipToken
  )

  const hasPodDocument = useMemo(
    () => (documents?.POD?.length ?? 0) > 0,
    [documents?.POD?.length]
  )

  const hasPaymentReceipt = useMemo(
    () => (paymentDocuments?.PAYMENT_RECEIPT?.length ?? 0) > 0,
    [paymentDocuments?.PAYMENT_RECEIPT?.length]
  )

  const handleDialogOpen = () => {
    setDialogOpen(true)
  }

  const handleDialogClose = () => {
    setDialogOpen(false)
  }

  const handleEdit = () => {
    navigate(`${listBasePath}/${invoice.id}`)
  }

  const handleDelete = async () => {
    await deleteInvoice({ id: invoice.id }).unwrap()
    setDeleteOpen(false)
  }

  const handleMarkAsPaid = async () => {
    await updateInvoiceStatus({
      id: invoice.id,
      status: 'PAID',
    }).unwrap()
  }

  const handleMarkAsUnpaid = async () => {
    await updateInvoiceStatus({
      id: invoice.id,
      status: 'NOT_PAID',
    }).unwrap()
  }

  const handleMarkAsToInvoice = async () => {
    await updateInvoiceStatus({
      id: invoice.id,
      status: 'TO_BE_INVOICED',
    }).unwrap()
  }

  const handleMoreMenuClose = () => {
    setMoreMenuAnchor(null)
  }

  return (
    <>
      <StyledTableCell sx={{ minWidth }} align={align}>
        <Stack direction="row" alignItems="center" spacing={1}>
          {invoiceType === 'NOT_PAID' && (
            <SubmitLockButton
              sx={{
                width: '150px',
                height: '36px',
                borderRadius: '6px',
                textTransform: 'capitalize',
                bgcolor: hasPaymentReceipt ? 'primary.main' : 'grey.300',
                color: hasPaymentReceipt ? 'common.white' : 'grey.700',
                '&:hover': {
                  bgcolor: hasPaymentReceipt ? 'primary.dark' : 'grey.400',
                },
              }}
              variant="contained"
              disabled={isMarkPaidLoading}
              onClick={handleMarkAsPaid}
            >
              Mark as Paid
            </SubmitLockButton>
          )}
          {invoiceType === 'PAID' && (
            <SubmitLockButton
              sx={{
                width: '150px',
                height: '36px',
                borderRadius: '6px',
                textTransform: 'capitalize',
                bgcolor: 'primary.main',
                color: 'common.white',
                whiteSpace: 'nowrap',
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
              }}
              variant="contained"
              disabled={isMarkPaidLoading}
              onClick={handleMarkAsUnpaid}
            >
              Mark as Unpaid
            </SubmitLockButton>
          )}
          {isToBeInvoicedWorkspaceTab(invoiceType) && (
            <Button
              sx={{
                width: '150px',
                height: '36px',
                borderRadius: '6px',
                textTransform: 'capitalize',
                bgcolor: hasPodDocument ? 'primary.main' : 'grey.300',
                color: hasPodDocument ? 'common.white' : 'grey.700',
                '&:hover': {
                  bgcolor: hasPodDocument ? 'primary.dark' : 'grey.400',
                },
              }}
              variant="contained"
              disabled={disabled}
              onClick={handleDialogOpen}
            >
              Send Invoice
            </Button>
          )}

          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={1}
          >
            {invoiceType !== 'PAID' && (
              <img
                src={Message}
                alt="view"
                width={33}
                height={33}
                style={{ cursor: 'pointer', marginTop: 4 }}
                onClick={() => {
                  dispatch(setSelectedOrderId(invoice?.orderId))
                  dispatch(setSelectedInvoiceId(invoice?.id))
                  handleViewInvoice(1) // Pass 1 to show Invoice tab (index 1)
                }}
              />
            )}
            {invoiceType === 'NOT_PAID' && (
              <>
                <Tooltip title="More">
                  <IconButton
                    aria-label="More"
                    onClick={(e) => setMoreMenuAnchor(e.currentTarget)}
                    size="small"
                    sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}
                  >
                    <MoreHorizIcon />
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={moreMenuAnchor}
                  open={moreMenuOpen}
                  onClose={handleMoreMenuClose}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <MenuItem
                    disabled={disabled}
                    onClick={() => {
                      handleMoreMenuClose()
                      handleDialogOpen()
                    }}
                  >
                    Resend Invoice
                  </MenuItem>
                  <MenuItem
                    disabled={isMarkPaidLoading}
                    onClick={() => {
                      handleMoreMenuClose()
                      handleMarkAsToInvoice()
                    }}
                  >
                    Mark as To Invoice
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleMoreMenuClose()
                      handleEdit()
                    }}
                  >
                    Edit
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleMoreMenuClose()
                      setDeleteOpen(true)
                    }}
                    sx={{ color: 'error.main' }}
                  >
                    Delete
                  </MenuItem>
                </Menu>
              </>
            )}
          {invoiceType !== 'NOT_PAID' && invoiceType !== 'PAID' && (
              <>
                <img
                  src={Edit}
                  alt="edit"
                  width={36}
                  height={36}
                  style={{ cursor: 'pointer' }}
                  onClick={handleEdit}
                />
                <img
                  src={Delete}
                  alt="delete"
                  width={28}
                  height={33}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setDeleteOpen(true)}
                />
              </>
            )}
          </Stack>
        </Stack>
      </StyledTableCell>
      <InvoiceTableEmailDialog
        open={dialogOpen}
        invoice={invoice}
        handleClose={handleDialogClose}
      />
      <ConfirmationDialog
        title="Delete Invoice"
        open={deleteOpen}
        handleClose={() => setDeleteOpen(false)}
        successBtnText="Delete"
        successBtnTextStyle={{ color: 'error' }}
        handleSuccessBtn={handleDelete}
      >
        Are you sure to delete the invoice{' '}
        <strong>{invoice.userInvoiceId}</strong>
      </ConfirmationDialog>
    </>
  )
}

export default InvoiceTableActions
